"use client";

import { deleteCookie, getCookie, setCookie } from "cookies-next/client";
import { useRouter } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { SESSION_ENDED_EVENT } from "@/lib/api";
import { logoutService } from "@/services/auth.service";
import type { LoginResponse, User } from "@/types/auth";

const ACCESS_TOKEN_COOKIE = "access_token";
const USER_DATA_COOKIE = "user_data";
const ONE_DAY_IN_SECONDS = 60 * 60 * 24;

function readStorageItem(key: string) {
  if (typeof window === "undefined") return null;

  return window.localStorage.getItem(key);
}

function writeStorageItem(key: string, value: string) {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(key, value);
}

function removeStorageItem(key: string) {
  if (typeof window === "undefined") return;

  window.localStorage.removeItem(key);
}

function parseStoredUser(userData: string | null | undefined) {
  if (!userData) return null;

  try {
    return JSON.parse(userData) as User;
  } catch {
    try {
      return JSON.parse(decodeURIComponent(userData)) as User;
    } catch {
      return null;
    }
  }
}

interface SessionContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (response: LoginResponse) => void;
  updateUser: (user: User) => void;
  logout: () => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(
  undefined,
);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  const restoreSession = useCallback(() => {
    try {
      const token =
        readStorageItem(ACCESS_TOKEN_COOKIE) ??
        getCookie(ACCESS_TOKEN_COOKIE)?.toString() ??
        null;
      const userData =
        readStorageItem(USER_DATA_COOKIE) ??
        getCookie(USER_DATA_COOKIE)?.toString() ??
        null;
      const parsedUser = parseStoredUser(userData);

      if (token && parsedUser) {
        writeStorageItem(ACCESS_TOKEN_COOKIE, token);
        writeStorageItem(USER_DATA_COOKIE, JSON.stringify(parsedUser));
        setCookie(ACCESS_TOKEN_COOKIE, token, {
          maxAge: ONE_DAY_IN_SECONDS,
          path: "/",
          sameSite: "lax",
        });
        setCookie(USER_DATA_COOKIE, JSON.stringify(parsedUser), {
          maxAge: ONE_DAY_IN_SECONDS,
          path: "/",
          sameSite: "lax",
        });
        setUser(parsedUser);
      } else {
        removeStorageItem(ACCESS_TOKEN_COOKIE);
        removeStorageItem(USER_DATA_COOKIE);
        deleteCookie(ACCESS_TOKEN_COOKIE, { path: "/" });
        deleteCookie(USER_DATA_COOKIE, { path: "/" });
        setUser(null);
      }
    } catch (error) {
      console.error("Falha ao restaurar sessao", error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  // Várias requisições podem falhar juntas: avisa e redireciona uma única vez
  const isEndingSession = useRef(false);

  useEffect(() => {
    const handleSessionEnded = (event: Event) => {
      if (isEndingSession.current) return;
      isEndingSession.current = true;

      removeStorageItem(ACCESS_TOKEN_COOKIE);
      removeStorageItem(USER_DATA_COOKIE);
      deleteCookie(ACCESS_TOKEN_COOKIE, { path: "/" });
      deleteCookie(USER_DATA_COOKIE, { path: "/" });
      setUser(null);

      const message = (event as CustomEvent<string | undefined>).detail;
      toast.warning(message || "Sua sessão expirou. Faça login novamente.", {
        duration: 10000,
      });
      router.push("/");
    };

    window.addEventListener(SESSION_ENDED_EVENT, handleSessionEnded);
    return () =>
      window.removeEventListener(SESSION_ENDED_EVENT, handleSessionEnded);
  }, [router]);

  const signIn = useCallback((response: LoginResponse) => {
    isEndingSession.current = false;

    if (response.api_jwt?.access_token && response.user) {
      const token = response.api_jwt.access_token;
      const serializedUser = JSON.stringify(response.user);

      writeStorageItem(ACCESS_TOKEN_COOKIE, token);
      writeStorageItem(USER_DATA_COOKIE, serializedUser);
      setCookie(ACCESS_TOKEN_COOKIE, token, {
        maxAge: ONE_DAY_IN_SECONDS,
        path: "/",
        sameSite: "lax",
      });
      setCookie(USER_DATA_COOKIE, serializedUser, {
        maxAge: ONE_DAY_IN_SECONDS,
        path: "/",
        sameSite: "lax",
      });
      setUser(response.user);
    }
  }, []);

  const updateUser = useCallback((updatedUser: User) => {
    const serializedUser = JSON.stringify(updatedUser);

    writeStorageItem(USER_DATA_COOKIE, serializedUser);
    setCookie(USER_DATA_COOKIE, serializedUser, {
      maxAge: ONE_DAY_IN_SECONDS,
      path: "/",
      sameSite: "lax",
    });
    setUser(updatedUser);
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutService();
    } catch (error) {
      console.error("Erro ao fazer logout", error);
    } finally {
      removeStorageItem(ACCESS_TOKEN_COOKIE);
      removeStorageItem(USER_DATA_COOKIE);
      deleteCookie(ACCESS_TOKEN_COOKIE, { path: "/" });
      deleteCookie(USER_DATA_COOKIE, { path: "/" });
      setUser(null);
      router.push("/");
    }
  }, [router]);

  return (
    <SessionContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        signIn,
        updateUser,
        logout,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error("useSession deve ser usado dentro de um SessionProvider");
  }
  return context;
}
